import kratos from '@/utils/ory-client'
import { appendWorkspaceScopeToUrl, getStoredActiveTeamId } from '@/utils/workspace-scope'

export const READ_ONLY_TEAM_ERROR_EVENT = 'ff:readonly-team-error'

export type ReadOnlyTeamErrorDetail = {
  message: string
  teamName?: string | null
  ownerEmail?: string | null
}

/**
 * Centralized API error handler that detects 401 Unauthorized errors
 * and automatically logs out the user by redirecting to login
 */
export async function handleApiError(error: any, router?: any) {
  console.error('API Error:', error)
  
  // Check if it's a 401 Unauthorized error
  if (error?.status === 401 || error?.response?.status === 401) {
    // 401 Unauthorized detected, logging out user
    
    try {
      // Perform logout using Kratos
      const { data: logoutFlow } = await kratos.createBrowserLogoutFlow(
        undefined,
        {
          withCredentials: true,
          headers: {
            Accept: 'application/json'
          }
        }
      )

      await kratos.updateLogoutFlow(
        {
          token: logoutFlow.logout_token
        },
        {
          withCredentials: true,
          headers: {
            Accept: 'application/json'
          }
        }
      )

      // Redirect to login page
      if (typeof window !== 'undefined') {
        window.location.href = '/login'
      } else if (router) {
        router.push('/login')
      }
    } catch (logoutError) {
      console.error('Logout failed:', logoutError)
      // Still redirect to login on error
      if (typeof window !== 'undefined') {
        window.location.href = '/login'
      } else if (router) {
        router.push('/login')
      }
    }
  }
}

/**
 * Wrapper for fetch that automatically handles 401 errors
 */
export async function fetchWithAuth(
  url: string,
  options: RequestInit = {},
  router?: any,
  opts?: { skipUnauthorizedRedirect?: boolean }
): Promise<Response> {
  try {
    const scopedUrl = appendWorkspaceScopeToUrl(url, getStoredActiveTeamId())
    const response = await fetch(scopedUrl, {
      ...options,
      credentials: 'include' // Ensure cookies are included
    })

    // Broadcast a global read-only modal event for friendly UX.
    if (response.status === 403 && typeof window !== 'undefined') {
      try {
        const payload = (await response.clone().json()) as { message?: string } | null
        const rawMessage = String(payload?.message || '').trim()
        if (rawMessage.toLowerCase().includes('read-only permission for this team')) {
          const teamMatch = rawMessage.match(/\(([^)]+)\)/)
          const ownerMatch = rawMessage.match(/owner\s+\(([^)]+)\)/i)
          const detail: ReadOnlyTeamErrorDetail = {
            message: rawMessage,
            teamName: teamMatch?.[1] || null,
            ownerEmail: ownerMatch?.[1] || null,
          }
          window.dispatchEvent(new CustomEvent<ReadOnlyTeamErrorDetail>(READ_ONLY_TEAM_ERROR_EVENT, { detail }))
        }
      } catch {
        // ignore non-JSON 403 responses
      }
    }

    // If 401, handle the error
    if (response.status === 401) {
      if (!opts?.skipUnauthorizedRedirect) {
        await handleApiError({ status: 401 }, router)
        throw new Error('Unauthorized - user logged out')
      }
    }

    return response
  } catch (error) {
    // Handle network errors or other issues
    if (error instanceof TypeError && error.message.includes('fetch')) {
      console.error('Network error:', error)
    }
    throw error
  }
}
